import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axiosClient from '../../services/axiosClient';
import './ConsultantJobDetails.css';

 function hasValidSession() {
   try {
     const raw = window.localStorage.getItem('userDetails');
     if (!raw) return false;
     const data = JSON.parse(raw);
     if (!data?.token) return false;
     if (data?.expireDate) {
       const exp = new Date(data.expireDate);
       if (!Number.isNaN(exp.getTime()) && new Date() > exp) return false;
     }
     return true;
   } catch (_) {
     return false;
   }
 }

function getAuthHeader() {
  try {
    const storages = [window.localStorage, window.sessionStorage];
    const keys = ['authData','auth','userData','senoffre_auth','loginData','currentUser','user'];
    for (const storage of storages) {
      for (const key of keys) {
        const raw = storage.getItem(key);
        if (!raw) continue;
        const data = JSON.parse(raw);
        if (data?.token) return { Authorization: `${data.tokenType || 'Bearer'} ${data.token}` };
      }
    }
    return {};
  } catch (_) {
    return {};
  }
}

const ConsultantJobDetails = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const id = searchParams.get('id');
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

   const handleApplyClick = () => {
     const target = `/consultant/job-apply?id=${encodeURIComponent(id)}`;
     if (!hasValidSession()) {
       navigate(`/login?redirect=${encodeURIComponent(target)}`);
       return;
     }
     navigate(target);
   };

  useEffect(() => {
    if (!id) {
      setError("Aucune offre sélectionnée.");
      setLoading(false);
      return;
    }

    (async () => {
      try {
        setLoading(true);
        setError('');
        const resp = await axiosClient.get(`/posts/${id}`, {
          headers: { 'Accept': 'application/json', ...getAuthHeader() }
        });
        const data = resp.data;
        const payload = data?.result || data?.data || data;
        setJob(payload);
      } catch (e) {
        const msg = e?.message || 'Erreur réseau';
        setError(`Impossible de charger l'offre: ${msg}`);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch { return dateString; }
  };

  if (loading) {
    return (
      <div className="cjd-container">
        <div className="cjd-skeleton">
          <div className="cjd-skel-hero" />
          <div className="cjd-skel-grid">
            <div className="cjd-skel-card" />
            <div className="cjd-skel-card side" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cjd-container">
        <div className="cjd-error">
          <i className="fa fa-exclamation-triangle" />
          <div>
            <h4>Erreur de chargement</h4>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!job) return null;

  const logo = (() => {
    let logoUrl = job?.logo_url?.large || job?.logo_url?.full || job?.logo || '';
    // Fix localhost SSL issues
    if (logoUrl && logoUrl.includes('https://localhost:8000')) {
      logoUrl = logoUrl.replace('https://localhost:8000', 'http://localhost:8000');
    }
    // Ne pas retourner l'image par défaut
    if (logoUrl && logoUrl.includes('default/picture.jpg')) {
      return '';
    }
    return logoUrl;
  })();
  const title = job?.title || '—';
  const org = job?.company_name || '—';
  const created = formatDate(job?.created_at);
  const location = job?.city || job?.location || '';
  const salary = job?.salary_formatted || 'Non spécifié';
  const visits = job?.visits_formatted || '';

  return (
    <div className="cjd-container">
      <div className="cjd-topbar">
        <button className="cjd-back" onClick={() => navigate(-1)}>
          <i className="fa fa-arrow-left" /> Retour
        </button>
      </div>

      {/* Hero */}
      <section className="cjd-hero">
        <div className="cjd-hero-bg" />
        <div className="cjd-hero-card">
          <div className="cjd-hero-left">
            <div className="cjd-logo">
              {logo ? (
                <img 
                  src={logo} 
                  alt={org}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallback = document.createElement('span');
                    fallback.textContent = (org || '??').substring(0,2).toUpperCase();
                    e.target.parentNode.appendChild(fallback);
                  }}
                />
              ) : (
                <span>{(org || '??').substring(0,2).toUpperCase()}</span>
              )}
            </div>
            <div className="cjd-hero-info">
              <h1 className="cjd-title">{title}</h1>
              <div className="cjd-company">{org}</div>
              <div className="cjd-meta">
                {location && (
                  <span><i className="fa fa-map-marker" /> {location}</span>
                )}
                {created && (
                  <span><i className="fa fa-calendar" /> Publié le {created}</span>
                )}
                {visits && (
                  <span><i className="fa fa-eye" /> {visits}</span>
                )}
              </div>
              <div className="cjd-badges">
                <span className="cjd-badge active">Actif</span>
                <span className="cjd-badge subtle">Consultant</span>
              </div>
            </div>
          </div>
          <div className="cjd-hero-actions">
            {job?.application_url ? (
              <a href={job.application_url} target="_blank" rel="noreferrer" className="cjd-apply">
                Postuler <i className="fa fa-arrow-right" />
              </a>
            ) : (
              <button className="cjd-apply" onClick={handleApplyClick}>
                Postulez
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="cjd-content">
        <div className="cjd-grid">
          <div className="cjd-main">
            <div className="cjd-card">
              <h3 className="cjd-section-title">Description</h3>
              <div className="cjd-description">
                {job?.description ? (
                  job.description.split('\n').map((p, i) => p.trim() ? <p key={i}>{p}</p> : null)
                ) : (
                  <p className="muted">—</p>
                )}
              </div>
            </div>
          </div>
          <aside className="cjd-side">
            <div className="cjd-card sticky">
              <h4 className="cjd-side-title">Résumé</h4>
              <ul className="cjd-summary-list">
                {salary && (
                  <li><span>Salaire</span><b>{salary}</b></li>
                )}
                {job?.reference && (
                  <li><span>Référence</span><b>{job.reference}</b></li>
                )}
                {job?.email && (
                  <li><span>Email</span><b>{job.email}</b></li>
                )}
                {job?.phone && (
                  <li><span>Téléphone</span><b>{job.phone}</b></li>
                )}
              </ul>
              <div className="cjd-side-actions">
                {job?.application_url ? (
                  <a href={job.application_url} target="_blank" rel="noreferrer" className="cjd-apply secondary">
                    Postulez
                  </a>
                ) : (
                  <button className="cjd-apply secondary" onClick={handleApplyClick}>
                    Postulez
                  </button>
                )}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default ConsultantJobDetails;
